import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LoginIcon from "@mui/icons-material/Login";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getToken } from "../../util/authUtils";

// An app such as AgentCrafty, ChatGPT or Claude asks to use this person's Timely365 account
// through the Timely365 MCP. Requests go through this site so the API names timely365.com as the issuer.
const REQUEST_KEYS = ["response_type", "client_id", "redirect_uri", "code_challenge", "code_challenge_method", "state", "scope", "resource"];

async function readJson(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (_error) {
    return {};
  }
}

const formatDate = (value) => (value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

// Opened without a connection request, the page lists the apps this person allowed so they can disconnect any of them.
function ConnectedApps({ authToken }) {
  const [connections, setConnections] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!authToken) return undefined;
    let alive = true;
    fetch("/oauth/connections", { headers: { authorization: `Bearer ${authToken}` } })
      .then(async (response) => {
        const payload = await readJson(response);
        if (!response.ok) throw new Error(payload.error_description || "Could not load your connected apps.");
        if (alive) setConnections(payload.connections || []);
      })
      .catch((err) => { if (alive) setError(err.message || "Could not load your connected apps."); });
    return () => { alive = false; };
  }, [authToken]);

  const disconnect = async (connection) => {
    setBusy(connection.id);
    setError("");
    try {
      const response = await fetch(`/oauth/connections/${encodeURIComponent(connection.id)}/disconnect`, {
        method: "POST",
        headers: { authorization: `Bearer ${authToken}` },
      });
      const payload = await readJson(response);
      if (!response.ok) throw new Error(payload.error_description || "Could not disconnect. Try again.");
      setConnections((current) => current.filter((item) => item.id !== connection.id));
    } catch (err) {
      setError(err.message || "Could not disconnect. Try again.");
    } finally {
      setBusy("");
    }
  };

  if (!authToken) {
    return (
      <>
        <Typography sx={{ color: "text.secondary" }}>Sign in to see which apps, such as ChatGPT or Claude, can use your Timely365 account.</Typography>
        <Button component={RouterLink} to={`/login?returnTo=${encodeURIComponent("/allow-access")}`} variant="contained" startIcon={<LoginIcon />} sx={{ alignSelf: "flex-start" }}>
          Sign in
        </Button>
      </>
    );
  }
  return (
    <>
      <Typography sx={{ color: "text.secondary" }}>
        These apps can see your own bookings and book for you when you ask. Disconnect one to stop it right away.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!connections && !error ? (
        <CircularProgress size={20} />
      ) : connections?.length === 0 ? (
        <Typography>No apps are connected.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {(connections || []).map((connection) => (
            <Stack key={connection.id} direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{connection.appName}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Connected {formatDate(connection.connectedAt)}{connection.lastUsedAt ? ` · last used ${formatDate(connection.lastUsedAt)}` : ""}
                </Typography>
              </Box>
              <Button variant="outlined" color="error" onClick={() => disconnect(connection)} disabled={Boolean(busy)}>
                {busy === connection.id ? "Disconnecting..." : "Disconnect"}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
}

export default function AllowAccessPage() {
  const location = useLocation();
  const request = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Object.fromEntries(REQUEST_KEYS.filter((key) => params.has(key)).map((key) => [key, params.get(key)]));
  }, [location.search]);
  const returnTo = `/allow-access${location.search}`;
  const authToken = getToken();
  const listOnly = !request.client_id;
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deciding, setDeciding] = useState("");

  useEffect(() => {
    if (listOnly) return undefined;
    let alive = true;
    fetch(`/oauth/request?${new URLSearchParams(request)}`)
      .then(async (response) => {
        const payload = await readJson(response);
        if (!response.ok) throw new Error(payload.error_description || "This connection link is not valid.");
        if (alive) setApp(payload);
      })
      .catch((err) => { if (alive) setError(err.message || "This connection link is not valid."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [request, listOnly]);

  const decide = async (allow) => {
    setDeciding(allow ? "allow" : "deny");
    setError("");
    try {
      const response = await fetch("/oauth/decision", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ request, allow }),
      });
      const payload = await readJson(response);
      if (!response.ok || !payload.redirectTo) throw new Error(payload.error_description || "Could not finish connecting. Try again.");
      window.location.assign(payload.redirectTo);
    } catch (err) {
      setError(err.message || "Could not finish connecting. Try again.");
      setDeciding("");
    }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "background.default", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Stack
          spacing={3}
          sx={{
            bgcolor: "background.paper",
            border: 1, borderColor: "divider",
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            boxShadow: "0 12px 30px rgba(34, 55, 74, 0.08)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LockOpenIcon sx={{ color: "primary.main", fontSize: 34 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
              {listOnly ? "Connected apps" : app ? `Allow ${app.clientName} to use Timely365?` : "Connect an app to Timely365"}
            </Typography>
          </Stack>

          {listOnly ? (
            <ConnectedApps authToken={authToken} />
          ) : loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Checking the app...</Typography>
            </Stack>
          ) : !app ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <Typography sx={{ color: "text.secondary" }}>
                It will send you back to <strong>{app.redirectHost}</strong>. Only allow apps you trust.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, color: "text.primary", "& li": { mb: 0.5 } }}>
                <li>See your workspaces, booking types and your own bookings</li>
                <li>Find your free time and people who have booked with you</li>
                <li>Book appointments and create booking types when you ask it to</li>
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                It acts as you, sees only your own bookings, and can do only what your account can. You can disconnect it anytime under{" "}
                <RouterLink to="/allow-access">Connected apps</RouterLink>.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              {authToken ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => decide(true)}
                    disabled={Boolean(deciding)}
                    startIcon={deciding === "allow" ? <CircularProgress color="inherit" size={18} /> : null}
                    sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                  >
                    Allow
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => decide(false)} disabled={Boolean(deciding)} sx={{ borderColor: "#22374a", color: "text.primary" }}>
                    Deny
                  </Button>
                </Stack>
              ) : (
                <Button
                  component={RouterLink}
                  to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
                  variant="contained"
                  startIcon={<LoginIcon />}
                  sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, alignSelf: "flex-start" }}
                >
                  Sign in to continue
                </Button>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
